import React, { useState } from 'react';
import { Button, Space, Modal, Card, message } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Service } from '@/apis';
import type { SystemLogDetailResponse } from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { SearchBar } from '@/components/SearchBar';
import { createTimeColumn, createLongTextColumn } from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

// 系统日志数据类型
type SystemLogData = SystemLogDetailResponse;

const SystemLog: React.FC = () => {
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 使用数据表格Hook
  const {
    data,
    loading,
    pagination,
    handleDelete: handleDeleteFromHook,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<SystemLogData>({
    fetchData: async (pageNum, pageSize, custom) => {
      const response = await Service.systemLogControllerGetList(
        pageNum,
        pageSize,
        undefined, // logType
        undefined, // module
        undefined, // userId
        undefined, // level
        undefined, // startTime
        undefined, // endTime
        custom || '' // custom
      );
      return {
        errNo: 0,
        data: {
          rows: response.data?.rows || [],
          count: response.data?.count || 0,
          pn: pageNum,
        },
      };
    },
    deleteData: async id => {
      await Service.systemLogControllerDelete(id);
    },
    searchOptions: {
      fields: ['username', 'module', 'action', 'description', 'userRole'],
    },
  });

  // 删除日志
  const handleDelete = async (record: SystemLogData) => {
    Modal.confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除这条系统日志吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          setDeleteLoading(true);
          await handleDeleteFromHook(record.id);
        } catch (error) {
          console.error('删除系统日志失败:', error);
          message.error('删除失败');
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  // 表格列定义
  const columns: ColumnsType<SystemLogData> = [
    {
      title: '日志ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      fixed: 'left',
    },
    {
      title: '操作者',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (text: string) => (
        <span style={{ color: '#1890ff', fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: '操作模块',
      dataIndex: 'module',
      key: 'module',
      width: 120,
      render: (text: string) => (
        <span
          style={{
            background: 'rgba(24, 144, 255, 0.1)',
            color: '#1890ff',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: '操作动作',
      dataIndex: 'action',
      key: 'action',
      width: 120,
    },
    {
      ...createLongTextColumn('日志描述', 'description', 200),
    },
    {
      title: '日志类型',
      dataIndex: 'logType',
      key: 'logType',
      width: 100,
      render: (logType: string) => {
        const typeMap: Record<string, { color: string; text: string }> = {
          LOGIN: { color: '#52c41a', text: '登录' },
          LOGOUT: { color: '#faad14', text: '登出' },
          OPERATION: { color: '#1890ff', text: '操作' },
          API_CALL: { color: '#722ed1', text: 'API调用' },
          ERROR: { color: '#ff4d4f', text: '错误' },
          SYSTEM_INFO: { color: '#13c2c2', text: '系统信息' },
          SECURITY: { color: '#eb2f96', text: '安全' },
        };
        const typeInfo = typeMap[logType] || { color: '#666', text: logType };
        return (
          <span
            style={{
              color: typeInfo.color,
              fontWeight: 500,
              background: `${typeInfo.color}15`,
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
            }}
          >
            {typeInfo.text}
          </span>
        );
      },
    },
    {
      title: '用户角色',
      dataIndex: 'userRole',
      key: 'userRole',
      width: 120,
      render: (text: string) => <span style={{ color: '#666' }}>{text}</span>,
    },
    {
      ...createTimeColumn('datetime'),
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size='small'>
          <Button
            type='link'
            danger
            size='small'
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            loading={deleteLoading}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 操作按钮配置
  const actionButtons: ActionButton<SystemLogData>[] = [];

  // 统计数据
  const statsData: DataStat[] = [
    {
      title: '总日志数',
      value: pagination.total,
      prefix: '📊',
    },
    {
      title: '今日日志',
      value: data.filter(log => {
        const today = new Date().toDateString();
        const logDate = new Date(log.createTime).toDateString();
        return today === logDate;
      }).length,
      prefix: '📅',
    },
    {
      title: '错误日志',
      value: data.filter(log => log.logType === 'ERROR').length,
      prefix: '⚠️',
    },
    {
      title: '登录日志',
      value: data.filter(log => log.logType === 'LOGIN').length,
      prefix: '🔐',
    },
  ];

  return (
    <div className={styles.container}>
      <Card>
        <DataStats stats={statsData} />
      </Card>

      <Card style={{ marginTop: 16 }}>
        <SearchBar
          search={search}
          placeholder='搜索用户名、模块、描述等'
          loading={loading}
        />

        <DataTable
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
          rowKey='id'
        />
      </Card>
    </div>
  );
};

export default SystemLog;
